import { getTeamsDateFormat, getTeamsTimeFormat } from "../utils/dateUtils";
import { CustomerMeeting } from "../../customerMeetings/store/CustomerMeeting";

const pl = {
    common: {
        newMeeting: "Nowe spotkanie",
        errorMessage: (errorDetails: string) => `Wystąpił błąd: ${errorDetails}`,
        loader: "Trwa wczytywanie danych...",
    },
    appOutsideMsTeams: {
        message: "Aplikacja musi być uruchomiona w kontekście Microsoft Teams",
    },
    logo: {
        corporation: "Logo mBank dla korporacji",
        retail: "Logo mBank dla klientów detalicznych",
        private: "Logo mBank dla private banking",
    },
    meetingModals: {
        closeButton: "Zamknij",
        startingHourLabel: "Start",
        endingHourLabel: "Koniec",
        titlePlaceholder: "Dodaj tytuł",
        customerNamePlaceholder: "Imię i nazwisko klienta",
        customerEmailPlaceholder: "Adres e-mail klienta",
        customerIdPlaceholder: "ID klienta",
        additionalAttendeeEmailsPlaceholder: "Adresy e-mail dodatkowych pracowników",
        englishCommunicationCheckboxLabel: "Wyślij komunikację w języku angielskim",
        addClient: "Dodaj klienta",
        add: "Dodaj",
        fullnamePlaceholder: "Imię i nazwisko",
        emailPlaceholder: "Adres e-mail",
        useVirtualAppointments: "Użyj Virtual Appointments",
        customers: "Klienci",
        consultants: "Dodatkowi pracownicy",
        externalConsultants: "Konsultanci zewnętrzni",
        thirdParties: "Osoby trzecie",
        consultantsToolip: "np. asystent/doradca/DR/DSM/BM/mTFI",
        externalConsultantsTooltip: "np. pracownicy kancelarii, z którymi mamy podpisane umowy",
        thirdPartiesTooltip: "np. prawnicy, mąż, sekretarka - osoby bez umocowania do rachunku",
        customersTooltip: "potencjalni klienci",
        validation: {
            invalidEmail: "Email jest błędny",
            invalidAttendees: "Jeden z podanych adresów email jest błędny lub nie należy do organizacji",
            invalidRegon: "Podany numer REGON jest błędny",
            invalidClientId: "Błędny format ID Klienta",
            invalidC6: "Błędny format numeru C6",
            invalidTitle: "Treść nie może zawierać znaków: <>",
            invalidSeparator: "Użyj średnika jako separatora",
            invalidMeetingEndingDate: (maxNumberOfDays: number) =>
                `Data zakończenia spotkania nie może wykraczać poza ${maxNumberOfDays} dni`,
        },
        corporate: {
            addContactPerson: "Dodaj osobę kontaktową",
            companyNamePlaceholder: "Nazwa firmy",
            c6NumberPlaceholder: "Numer C6",
            regonPlaceholder: "Numer REGON",
        },
    },
    createMeeting: {
        header: "Nowe spotkanie",
        selectBusinessLinePlaceholder: "Wybierz linię biznesową",
        loadingBusinessLines: "Trwa ładowanie linii biznesowych...",
        noBusinessLineFound: "Utworzenie spotkania nie jest możliwe, ponieważ nie należysz do żadnej linii biznesowej.",
        createButton: "Utwórz spotkanie",
        adding: "Trwa tworzenie spotkania...",
    },
    updateMeeting: {
        header: "Edytuj spotkanie",
        updateButton: "Edytuj spotkanie",
        updating: "Trwa aktualizowanie spotkania...",
        newAdditionalAttendeeEmailsPlaceholder: "Dodatkowe adresy e-mail dodatkowych pracowników",
        save: "Zapisz",
    },
    meetingsList: {
        noMeetings: "Brak zaplanowanych spotkań",
        headers: {
            date: "Data spotkania",
            attendees: "Klienci",
            subject: "Temat spotkania",
        },
        cancelActionButton: "Anuluj",
        cancelDialog: {
            message: (meeting: CustomerMeeting) =>
                `Czy na pewno chcesz anulować spotkanie ${meeting.customers
                    .map((customer) => customer.name)
                    .join(", ")} (${getTeamsDateFormat(meeting.startAt)} w godzinach ${getTeamsTimeFormat(
                    meeting.startAt
                )} - ${getTeamsTimeFormat(meeting.endAt)})?`,
            additionalMessage: "Wszyscy uczestnicy spotkania otrzymają wiadomość z informacją o jego anulowaniu.",
            header: "Czy chcesz anulować?",
            cancelButton: "Wyjdź",
            confirmButton: "Potwierdź",
        },
        updateButton: "Edytuj",
        joinButton: "Rozpocznij",
        loading: "Trwa ładowanie spotkań...",
        updateMeetingSuccessAlert: "Spotkanie zostało pomyślnie zaktualizowane",
        updateMeetingTypeButton: "Typ spotkania",
    },
    businessLines: {
        private: "Private",
        corporate: "Korpo",
        retail: "Detal",
    },
    organizerNoRecordingWarning: {
        content: (time: string) =>
            `Nagrywanie zostało przerwane. Włącz je w ciągu ${time} sekund - jeśli tego nie zrobisz, spotkanie zakończy się automatycznie.`,
    },
    inMeetingAppConfig: {
        header: "Zdalne spotkania z klientami",
        info: "Aplikacja służąca do obsługi procesu zdalnych spotkań z klientami",
    },
    beforeMeeting: {
        header: "Zdalne spotkanie z klientem",
        organizatorSubHeader: "Jesteś organizatorem tego spotkania",
        presenterSubHeader: "Jesteś konsultantem na tym spotkaniu",
        rulesHeader: "Przed rozpoczęciem spotkania z klientem (wpuszczeniem klienta do pokoju rozmowy):",
        setBackgroundRule: "Ustaw bankowe logo jako tło.",
        rememberHeader: "Pamiętaj!",
        cautionHeader: "Uwaga!",
        notEnoughDiskSpace:
            "Nie masz odpowiednio dużo miejsca na nagranie spotkania w OneDrive. Potrzebujesz 1GB miejsca. Usuń zbędne pliki ze swojego OneDrive.",
        meetingRules: {
            header: "W spotkaniach z klientem:",
            noDesktopSharingRule:
                "Nigdy nie udostępniaj pulpitu. Możesz udostępniać wyłącznie dozwolone aplikacje, wskazane w procedurze pracy.",
            noLinksOpenRule: "Nie otwieraj żadnych linków przesyłanych na czacie.",
        },
        organizerRules: {
            recordRule: "Rozpocznij nagrywanie spotkania.",
            alwaysRecordRule: "Wyłącznie nagrywania spotkania spowoduje jego natychmiastowe przerwanie.",
        },
    },
    duringMeeting: {
        organizerView: {
            header: "Checklista spotkania",
            freeSpaceRequirement: "1GB wolnego miejsca na OneDrive",
            isRecordingRequirement: "Organizator włączył nagrywanie",
            identityRequirement: "Potwierdź tożsamość klienta",
        },
        presenterView: {
            header: "Jesteś konsultantem na tym spotkaniu",
        },
    },
    organizerMeetingTypePrompt: {
        headerOnStartNotAnOrganizer: "Organizator wypełnia dane na temat spotkania",
        headerOnStartOrganizer: "Wybierz rodzaj rozmowy",
        headerOnFinish: "Dziękujemy za wypełnienie ankiety",
        placeholderMeetingType: "Typ rozmowy",
        save: "Zapisz",
    },
    customerJoinsLobbyPrompt: {
        content:
            "Musimy nagrywać spotkania z klientami. WŁĄCZ NAGRYWANIE, zanim klient dołączy do spotkania. Jeśli tego nie zrobisz lub przerwiesz nagrywanie – spotkanie automatycznie się zakończy.",
    },
    screenSharingWarningPrompt: {
        defaultContent: "Pamiętaj, aby podczas rozmowy udostępniać tylko dozwolone pliki i aplikacje.",
        privateContent:
            "Pamiętaj, aby podczas rozmowy udostępniać tylko dozwolone pliki i aplikacje. Jeśli chcesz udostępnić Aplikację Doradcy, ustaw w niej tryb wideo.",
    },
    peoplePicker: {
        enterCharacters: "Wpisz co najmniej 3 znaki",
        noResults: "Nie znaleziono",
        loading: "Wyszukiwanie",
        moreResults:
            "Znaleziono więcej wyników, spróbuj doprecyzować zapytanie jeśli lista nie zawiera szukanego elementu.",
    },
};

export default pl;
